<div class="container-fluid py-md-4 main">
  <!-- Header Start -->
  <div
    class="dash-header d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
    <!-- Left: Welcome -->
    <div class="welcome">
      <h4 class="mb-1 fw-semibold text-teal">
        Welcome Back, <span class="fw-bold">{{ loggedInUserName || 'Guest' }}.</span>
      </h4>
      <p class="mb-0 text-muted small">
        Here’s what’s happening with your clinic today — quick stats, due protocols, and alerts.
      </p>
    </div>

    <!-- Right: Actions / Quick Info -->
    <div class="right-actions d-flex flex-wrap align-items-center justify-content-md-end gap-2">
      <!-- Search -->
      <div class="input-group input-group-sm dash-search">
        <span class="input-group-text bg-white border-end-0">
          <i class="bi bi-search"></i>
        </span>
        <input type="text" class="form-control border-start-0" placeholder="Search patients, orders..." />
      </div>

      <!-- Date pill -->
      <div class="dropdown d-inline-block ms-1" ngbDropdown>
        <button type="button" class="btn btn-soft btn-sm d-inline-flex align-items-center gap-2 no-caret"
          ngbDropdownToggle id="page-header-calendar-dropdown">
          <i class="bi bi-calendar3"></i>
          <span>{{ selectedDateLabel }}</span>
        </button>

        <div class="dropdown-menu dropdown-menu-end p-2" ngbDropdownMenu
          aria-labelledby="page-header-calendar-dropdown">

          <div class="mb-2 d-flex justify-content-between align-items-center gap-2">
            <small class="text-muted">Select date to filter stats</small>

            <button type="button" class="btn btn-sm btn-outline-secondary" (click)="clearDateFilter()">
              Reset
            </button>
          </div>

          <ngb-datepicker [ngModel]="selectedDate" (ngModelChange)="onDateSelect($event)">
          </ngb-datepicker>
        </div>
      </div>

      <!-- Add Order -->
      <button class="btn btn-teal btn-sm d-inline-flex align-items-center gap-2" routerLink="/order/list">
        <i class="bi bi-plus-lg"></i>
        <span>New Order</span>
      </button>

      <!-- Notifications -->
      <button class="btn btn-soft btn-sm position-relative" routerLink="/notifications/me">
        <i class="bi bi-bell"></i>
      </button>
    </div>
  </div>
  <!-- Header End -->

  <!-- FIRST ROW STATS CARDS START -->
  <div class="row g-3 mt-2">
    <!-- Card 1 -->
    <div class="col-12 col-sm-6 col-xl-3">
      <div class="kpi-card" routerLink="/patientManagement/list">
        <div class="kpi-icon">
          <i class="bi bi-people"></i>
        </div>

        <div class="kpi-body">
          <div class="kpi-label">Total Patients</div>
          <div class="kpi-value">
            <ng-container *ngIf="!isStatsLoading; else patientsLoading">
              {{ kpis.totalPatients | number }}
            </ng-container>
            <ng-template #patientsLoading>...</ng-template>
          </div>
        </div>
      </div>
    </div>

    <!-- Card 2 -->
    <div class="col-12 col-sm-6 col-xl-3">
      <div class="kpi-card" routerLink="/care-protocol/list">
        <div class="kpi-icon">
          <i class="bi bi-journal-medical"></i>
        </div>

        <div class="kpi-body">
          <div class="kpi-label">Active Protocols</div>
          <div class="kpi-value">
            <ng-container *ngIf="!isStatsLoading; else protocolsLoading">
              {{ kpis.activeProtocols | number }}
            </ng-container>
            <ng-template #protocolsLoading>...</ng-template>
          </div>
        </div>
      </div>
    </div>

    <!-- Card 3 -->
    <div class="col-12 col-sm-6 col-xl-3">
      <div class="kpi-card" routerLink="/order/list">
        <div class="kpi-icon">
          <i class="bi bi-bag-check"></i>
        </div>

        <div class="kpi-body">
          <div class="kpi-label">Completed Orders</div>

          <div class="d-flex align-items-end justify-content-between gap-2">
            <div class="kpi-value">
              <ng-container *ngIf="!isStatsLoading; else completedLoading">
                {{ kpis.completedOrders | number }}
              </ng-container>
              <ng-template #completedLoading>...</ng-template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Card 4 -->
    <div class="col-12 col-sm-6 col-xl-3">
      <div class="kpi-card alert-card" routerLink="/order/list">
        <div class="kpi-icon warn">
          <i class="bi bi-exclamation-triangle"></i>
        </div>

        <div class="kpi-body">
          <div class="kpi-label">Orders On Hold</div>
          <div class="kpi-value">
            <ng-container *ngIf="!isStatsLoading; else holdLoading">
              {{ kpis.ordersOnHold | number }}
            </ng-container>
            <ng-template #holdLoading>...</ng-template>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- FIRST ROW STATS CARDS END -->

  <!-- MIDDLE SECTION DUE PROTOCOL TABLE AND QUICK VIEW CHART START -->
  <div class="row g-3 mt-2">

    <!-- LEFT : Due Protocols Table -->
    <div class="col-12 col-xl-8">
      <div class="section-card">

        <!-- Header -->
        <div class="section-header d-flex align-items-center justify-content-between">
          <h6 class="section-title">Due Protocols</h6>
          <a href="#" class="view-all btn btn-sm">
            View All <i class="bi bi-arrow-right-short"></i>
          </a>
        </div>

        <!-- Table -->
        <div class="table-responsive">
          <table class="table align-middle mb-0 due-table">
            <thead>
              <tr>
                <th style="min-width:260px;">Patient</th>
                <th class="d-none d-md-table-cell" style="min-width:150px;">Protocol</th>
                <th style="min-width:170px;">Next Due</th>
                <th style="min-width:140px;">Status</th>
                <th class="text-end" style="min-width:110px;">Actions</th>
              </tr>
            </thead>

            <tbody>
              <tr *ngIf="isDueProtocolsLoading">
                <td colspan="5" class="text-center py-4 text-muted">
                  Loading due protocols...
                </td>
              </tr>

              <tr *ngIf="!isDueProtocolsLoading && dueProtocols.length === 0">
                <td colspan="5" class="text-center py-4 text-muted">
                  No due protocols found.
                </td>
              </tr>

              <tr *ngFor="let protocol of dueProtocols">
                <td>
                  <div class="d-flex align-items-center gap-3">
                    <div class="avatar-soft">
                      {{ getInitials(protocol.patientName) }}
                    </div>

                    <div class="minw-0">
                      <div class="d-flex align-items-center gap-2">
                        <span class="patient-name text-truncate">
                          {{ protocol.patientName || 'N/A' }}
                        </span>
                      </div>

                      <div class="patient-meta text-truncate">
                        <i class="bi bi-telephone"></i> {{ protocol.phone || '-' }}
                        <span class="dot"></span>
                        <i class="bi" [ngClass]="protocol.fulfillmentPreference === 'shipping' ? 'bi-truck' : 'bi-bag'">
                        </i>
                        {{ getFulfillmentLabel(protocol.fulfillmentPreference) }}
                      </div>
                    </div>
                  </div>
                </td>

                <td class="d-none d-md-table-cell">
                  <span class="chip-soft">
                    <i class="bi bi-journal-medical"></i>
                    {{ protocol.templateName || (protocol.frequencyMonths + '-Month') }}
                  </span>
                </td>

                <td>
                  <div class="due-date">
                    <i class="bi bi-calendar3"></i>
                    <div>
                      <div class="fw-semibold" [ngClass]="protocol.dueState === 'OVERDUE' ? 'text-danger' : ''">
                        {{ getDueText(protocol.nextDueDate) }}
                      </div>
                      <div class="small text-muted">
                        {{ formatDisplayDate(protocol.nextDueDate) }}
                      </div>
                    </div>
                  </div>
                </td>

                <td>
                  <span class="status-pill" [ngClass]="getDueStatusClass(protocol)">
                    <i class="bi"
                      [ngClass]="protocol.dueState === 'OVERDUE' ? 'bi-exclamation-triangle' : 'bi-check-circle'">
                    </i>
                    {{ getDueStatusLabel(protocol) }}
                  </span>
                </td>

                <td class="text-end">
                  <div class="d-inline-flex gap-2">
                    <button class="icon-btn" title="View Profile">
                      <i class="bi bi-eye"></i>
                    </button>
                    <button class="icon-btn" title="Message">
                      <i class="bi bi-chat-dots"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </div>

    <!-- RIGHT : Order Overview -->
    <div class="col-12 col-xl-4">
      <div class="section-card h-100">

        <!-- Header -->
        <div class="section-header d-flex align-items-center justify-content-between">
          <h6 class="section-title">Order Status Overview</h6>

          <button class="btn btn-soft btn-sm d-flex align-items-center gap-1" type="button">
            {{ selectedDateLabel }}
            <i class="bi bi-chevron-down"></i>
          </button>
        </div>

        <!-- Chart Wrapper -->
        <div class="chart-wrapper position-relative">
          <div *ngIf="isOrderOverviewLoading" class="text-center py-5 text-muted">
            Loading chart...
          </div>

          <ng-container *ngIf="!isOrderOverviewLoading">
            <apx-chart [series]="statusChart.series" [chart]="statusChart.chart" [labels]="statusChart.labels"
              [colors]="statusChart.colors" [plotOptions]="statusChart.plotOptions"
              [dataLabels]="statusChart.dataLabels" [stroke]="statusChart.stroke" [legend]="statusChart.legend"
              [tooltip]="statusChart.tooltip">
            </apx-chart>

            <!-- Breakdown chips INSIDE chart -->
            <div class="donut-chips">
              <span class="chip pending">Pending <b>{{ orderOverview.pendingOrders }}</b></span>
              <span class="chip processing">Processing <b>{{ orderOverview.processingOrders }}</b></span>
              <span class="chip delivered">Delivered <b>{{ orderOverview.deliveredOrders }}</b></span>
              <span class="chip hold">On-Hold <b>{{ orderOverview.onHoldOrders }}</b></span>
            </div>
          </ng-container>
        </div>

      </div>
    </div>

  </div>
  <!-- MIDDLE SECTION DUE PROTOCOL TABLE AND QUICK VIEW CHART END -->

  <!-- Recent ORDERS AND PAYMENT STATUS START-->
  <div class="row g-3 mt-2">

    <!-- LEFT: Recent Orders -->
    <div class="col-12 col-xl-8">
      <div class="section-card">

        <div class="section-header d-flex align-items-center justify-content-between">
          <h6 class="section-title">Recent Orders</h6>
          <a class="view-all btn btn-sm" href="#">
            View All <i class="bi bi-arrow-right-short"></i>
          </a>
        </div>

        <div class="table-responsive">
          <table class="table align-middle mb-0 dash-table">
            <thead>
              <tr>
                <th style="min-width:170px;">Order</th>
                <th style="min-width:230px;">Patient</th>
                <th class="d-none d-md-table-cell" style="min-width:150px;">Fulfillment</th>
                <th style="min-width:130px;">Total</th>
                <th style="min-width:140px;">Status</th>
                <th class="text-end" style="min-width:110px;">Action</th>
              </tr>
            </thead>

            <tbody>
              <tr *ngIf="isRecentOrdersLoading">
                <td colspan="6" class="text-center py-4 text-muted">
                  Loading recent orders...
                </td>
              </tr>

              <tr *ngIf="!isRecentOrdersLoading && recentOrdersList.length === 0">
                <td colspan="6" class="text-center py-4 text-muted">
                  No recent orders found.
                </td>
              </tr>

              <tr *ngFor="let order of recentOrdersList">
                <td>
                  <div class="fw-bold text-teal">{{ order.orderNo }}</div>
                  <div class="small text-muted">
                    <i class="bi bi-clock"></i> {{ formatOrderTime(order.createdAt) }}
                  </div>
                </td>

                <td>
                  <div class="d-flex align-items-center gap-2">
                    <span class="avatar-mini">{{ getInitials(order.patientName) }}</span>
                    <div class="minw-0">
                      <div class="fw-semibold text-truncate">{{ order.patientName || 'N/A' }}</div>
                      <div class="small text-muted text-truncate">
                        <i class="bi bi-telephone"></i> {{ order.patientPhone || '-' }}
                      </div>
                    </div>
                  </div>
                </td>

                <td class="d-none d-md-table-cell">
                  <span class="chip-soft">
                    <i class="bi" [ngClass]="getFulfillmentIcon(order.fulfillmentMethod)"></i>
                    {{ getFulfillmentChipLabel(order.fulfillmentMethod) }}
                  </span>
                </td>

                <td>
                  <div class="fw-bold">{{ formatCurrency(order.grandTotal, order.currency) }}</div>
                  <div class="small text-muted">Disc: {{ order.discountPercent || 0 }}%</div>
                </td>

                <td>
                  <span class="status-pill" [ngClass]="getRecentOrderStatusClass(order.status)">
                    <i class="bi" [ngClass]="getRecentOrderStatusIcon(order.status)"></i>
                    {{ getRecentOrderStatusLabel(order.status) }}
                  </span>
                </td>

                <td class="text-end">
                  <button class="btn btn-soft btn-sm">
                    {{ order.status === 'DISPATCHED' ? 'Track' : 'View' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </div>

    <!-- RIGHT: Payment Issues -->
    <div class="col-12 col-xl-4">
      <div class="section-card h-100">

        <div class="section-header d-flex align-items-center justify-content-between">
          <h6 class="section-title">Payment Issues</h6>

          <button class="btn btn-soft btn-sm d-inline-flex align-items-center gap-2" type="button">
            <i class="bi bi-funnel"></i> Filter
          </button>
        </div>

        <!-- Issues list -->
        <div class="issues-list">

          <div *ngIf="isPaymentIssuesLoading" class="text-center py-4 text-muted">
            Loading payment issues...
          </div>

          <div *ngIf="!isPaymentIssuesLoading && paymentIssuesList.length === 0" class="text-center py-4 text-muted">
            No payment issues found.
          </div>

          <div class="issue-item" *ngFor="let issue of paymentIssuesList">
            <div class="issue-left">
              <div class="issue-icon" [ngClass]="getPaymentIssueSeverityClass(issue)">
                <i class="bi" [ngClass]="getPaymentIssueIconClass(issue)"></i>
              </div>

              <div class="minw-0">
                <div class="fw-semibold text-truncate">{{ issue.title }}</div>
                <div class="small text-muted text-truncate">
                  {{ issue.subtitle }}
                </div>
              </div>
            </div>

            <div class="issue-right text-end">
              <div class="fw-bold">
                {{ formatIssueAmount(issue.amount, issue.currency) }}
              </div>
              <button class="btn btn-teal btn-sm mt-1" routerLink="/billing-profile/list">
                {{ getPaymentIssueActionLabel(issue) }}
              </button>
            </div>
          </div>

        </div>

        <a class="view-all mt-2 d-inline-block btn btn-sm" routerLink="/notifications/payments&Issues">
          View All Issues <i class="bi bi-arrow-right-short"></i>
        </a>

      </div>
    </div>

  </div>
  <!-- Recent ORDER STATUS AND PAYMENT STATUS END-->

  <!-- LOW STOCK AND APPOINTMENTS START-->
  <div class="row g-3 mt-2">

    <!-- LEFT: Low Stock Alerts -->
    <div class="col-12 col-xl-8">
      <div class="section-card">

        <div class="section-header d-flex align-items-center justify-content-between">
          <h6 class="section-title">Low Stock Alerts</h6>

          <button class="btn btn-soft btn-sm d-inline-flex align-items-center gap-2">
            <i class="bi bi-funnel"></i> Filter
          </button>
        </div>

        <div class="alerts-list">

          <!-- item 1 -->
          <div class="alert-item">
            <div class="alert-left">
              <div class="alert-icon warn">
                <i class="bi bi-box-seam"></i>
              </div>

              <div class="minw-0">
                <div class="fw-semibold text-truncate">Minoxidil 5% Foam</div>
                <div class="small text-muted text-truncate">
                  SKU: MIN-5-FOAM • Category: Topicals
                </div>
              </div>
            </div>

            <div class="alert-right text-end">
              <div class="stock-pill danger badge">5 left</div>
              <button class="btn btn-teal btn-sm mt-2 ms-2">Restock</button>
            </div>
          </div>

          <!-- item 2 -->
          <div class="alert-item">
            <div class="alert-left">
              <div class="alert-icon warn">
                <i class="bi bi-capsule"></i>
              </div>

              <div class="minw-0">
                <div class="fw-semibold text-truncate">Biotin Supplements</div>
                <div class="small text-muted text-truncate">
                  SKU: BIO-60 • Category: Supplements
                </div>
              </div>
            </div>

            <div class="alert-right text-end">
              <div class="stock-pill warning badge">12 left</div>
              <button class="btn btn-teal btn-sm mt-2 ms-2">Restock</button>
            </div>
          </div>

          <!-- item 3 -->
          <div class="alert-item">
            <div class="alert-left">
              <div class="alert-icon warn">
                <i class="bi bi-droplet"></i>
              </div>

              <div class="minw-0">
                <div class="fw-semibold text-truncate">Restore Hair Serum</div>
                <div class="small text-muted text-truncate">
                  SKU: RHS-30 • Category: Serums
                </div>
              </div>
            </div>

            <div class="alert-right text-end">
              <div class="stock-pill warning badge">18 left</div>
              <button class="btn btn-teal btn-sm mt-2 ms-2">Restock</button>
            </div>
          </div>

        </div>

        <a class="view-all btn btn-sm mt-2 d-inline-block" href="#">
          View Inventory <i class="bi bi-arrow-right-short"></i>
        </a>

      </div>
    </div>

    <!-- RIGHT: Upcoming Appointments -->
    <div class="col-12 col-xl-4">
      <div class="section-card h-100">

        <div class="section-header d-flex align-items-center justify-content-between">
          <h6 class="section-title">Upcoming Appointments <sup class="text-warning" style="font-size: 8px;">Coming Soon
              (Phase 2)</sup></h6>

          <button class="btn btn-soft btn-sm d-inline-flex align-items-center gap-2">
            <i class="bi bi-calendar3"></i> Today
          </button>
        </div>

        <div class="appointments">

          <!-- appointment 1 -->
          <div class="appt-item">
            <div class="appt-time">
              <div class="time">10:30</div>
              <div class="ampm">AM</div>
            </div>

            <div class="appt-info minw-0">
              <div class="d-flex align-items-center justify-content-between gap-2">
                <div class="fw-semibold text-truncate">Follow-up Session</div>
                <span class="appt-badge">Clinic</span>
              </div>

              <div class="small text-muted text-truncate">
                <i class="bi bi-person"></i> Kedon Luke
                <span class="dot"></span>
                <i class="bi bi-geo-alt"></i> Room 2
              </div>
            </div>
          </div>

          <!-- appointment 2 -->
          <div class="appt-item">
            <div class="appt-time">
              <div class="time">12:15</div>
              <div class="ampm">PM</div>
            </div>

            <div class="appt-info minw-0">
              <div class="d-flex align-items-center justify-content-between gap-2">
                <div class="fw-semibold text-truncate">Protocol Review</div>
                <span class="appt-badge tele">Tele</span>
              </div>

              <div class="small text-muted text-truncate">
                <i class="bi bi-person"></i> Sarah Lee
                <span class="dot"></span>
                <i class="bi bi-camera-video"></i> Video Call
              </div>
            </div>
          </div>

          <!-- appointment 3 -->
          <div class="appt-item">
            <div class="appt-time">
              <div class="time">03:00</div>
              <div class="ampm">PM</div>
            </div>

            <div class="appt-info minw-0">
              <div class="d-flex align-items-center justify-content-between gap-2">
                <div class="fw-semibold text-truncate">New Consultation</div>
                <span class="appt-badge">Clinic</span>
              </div>

              <div class="small text-muted text-truncate">
                <i class="bi bi-person"></i> Mike Johnson
                <span class="dot"></span>
                <i class="bi bi-geo-alt"></i> Room 1
              </div>
            </div>
          </div>

        </div>

        <a class="view-all btn btn-sm mt-2 d-inline-block" href="#">
          View Calendar <i class="bi bi-arrow-right-short"></i>
        </a>

      </div>
    </div>

  </div>

  <!--STOCK AND APPOINTMENTS END-->



  <!-- Middle Section – Charts -->
  <!-- <div class="row g-3 g-md-4 mb-4">
    <div class="col-12 col-xl-6">
      <div class="card border-0 h-100">
        <div class="card-header bg-white border-0 fw-semibold">📈 Sales Trend</div>
        <div class="card-body">
          <apx-chart
            [series]="salesChart.series"
            [chart]="salesChart.chart"
            [xaxis]="salesChart.xaxis"
            [dataLabels]="salesChart.dataLabels"
            [colors]="salesChart.colors"
            [legend]="salesChart.legend"
            [stroke]="salesChart.stroke">
          </apx-chart>
        </div>
      </div>
    </div>

    <div class="col-12 col-md-6 col-xl-3">
      <div class="card border-0 h-100">
        <div class="card-header bg-white border-0 fw-semibold">💳 Payment Breakdown</div>
        <div class="card-body">
          <apx-chart
            [series]="paymentChart.series"
            [chart]="paymentChart.chart"
            [labels]="paymentChart.labels"
            [colors]="paymentChart.colors"
            [legend]="paymentChart.legend"
            [dataLabels]="paymentChart.dataLabels">
          </apx-chart>
        </div>
      </div>
    </div>

    <div class="col-12 col-md-6 col-xl-3">
      <div class="card border-0 h-100">
        <div class="card-header bg-white border-0 fw-semibold">📦 Order Status</div>
        <div class="card-body">
          <apx-chart
            [series]="statusChart.series"
            [chart]="statusChart.chart"
            [labels]="statusChart.labels"
            [colors]="statusChart.colors"
            [legend]="statusChart.legend"
            [dataLabels]="statusChart.dataLabels">
          </apx-chart>
        </div>
      </div>
    </div>
  </div> -->

  <!-- Top Customers -->
  <!-- <div class="card border-0 mb-4 mt-5">
    <div class="card-header bg-white border-0 fw-semibold d-flex justify-content-between align-items-center">
      <span>👑 Top Customers</span>
      <a class="btn btn-sm btn-outline-secondary">View All</a>
    </div>
    <div class="table-responsive">
      <table class="table table-hover align-middle mb-0">
        <thead class="table-light">
          <tr>
            <th>Customer</th>
            <th class="d-none d-md-table-cell">Email</th>
            <th class="text-end">Orders</th>
            <th class="text-end">Spend</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let c of topCustomers">
            <td>{{ c.name }}</td>
            <td class="d-none d-md-table-cell">{{ c.email }}</td>
            <td class="text-end">{{ c.orders | number }}</td>
            <td class="text-end">{{ c.spend | number }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div> -->

  <!-- Inventory Snapshot -->
  <!-- <div class="row g-3 g-md-4 mb-4">
    <div class="col-12 col-lg-5">
      <div class="card border-0 h-100">
        <div class="card-header bg-white border-0 fw-semibold">📦 Inventory Valuation</div>
        <div class="card-body">
          <div class="row text-center g-3">
            <div class="col-4">
              <div class="small text-muted">Total Qty</div>
              <div class="fw-bold fs-5">{{ inventoryValuation.totalQty | number }}</div>
            </div>
            <div class="col-4">
              <div class="small text-muted">Total Cost</div>
              <div class="fw-bold fs-5">{{ inventoryValuation.totalCost | number }}</div>
            </div>
            <div class="col-4">
              <div class="small text-muted">Retail Value</div>
              <div class="fw-bold fs-5">{{ inventoryValuation.totalRetail | number }}</div>
            </div>
          </div>
          <div class="mt-3 small text-muted">
            * Numbers are shown as plain values (no currency symbol).
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 col-lg-7">
      <div class="card border-0 h-100">
        <div class="card-header bg-white border-0 fw-semibold">⚠️ Low Stock Items</div>
        <div class="table-responsive">
          <table class="table table-striped align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th>SKU</th>
                <th>Product</th>
                <th class="text-end">Qty</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let i of lowStock">
                <td>{{ i.sku }}</td>
                <td>{{ i.name }}</td>
                <td class="text-end">{{ i.qty | number }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div> -->

  <!-- Returns / Refunds -->
  <!-- <div class="row g-3 g-md-4 mb-4">
    <div class="col-12 col-lg-6">
      <div class="card border-0  h-100">
        <div class="card-body text-center">
          <div class="small text-muted">Returns Count</div>
          <div class="display-6 fw-bold text-primary">{{ returns.count | number }}</div>
        </div>
      </div>
    </div>
    <div class="col-12 col-lg-6">
      <div class="card border-0 h-100">
        <div class="card-body text-center">
          <div class="small text-muted">Refund Amount</div>
          <div class="display-6 fw-bold text-danger">{{ returns.refundAmount | number }}</div>
        </div>
      </div>
    </div>
  </div> -->

  <!-- Bottom – Recent Activity -->
  <!-- <div class="row g-3 g-md-4">
    <div class="col-12 col-xl-7">
      <div class="card border-0 h-100">
        <div class="card-header bg-white border-0 fw-semibold">🧾 Recent Orders</div>
        <div class="table-responsive">
          <table class="table table-hover align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th>Order #</th>
                <th class="d-none d-lg-table-cell">Customer</th>
                <th class="text-end">Amount</th>
                <th class="d-none d-md-table-cell">Status</th>
                <th class="text-end d-none d-sm-table-cell">Date</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let o of recentOrders">
                <td>{{ o.id }}</td>
                <td class="d-none d-lg-table-cell">{{ o.customer }}</td>
                <td class="text-end">{{ o.amount | number }}</td>
                <td class="d-none d-md-table-cell">
                  <span class="badge"
                        [class.bg-secondary]="o.status==='Pending'"
                        [class.bg-primary]="o.status==='Processing'"
                        [class.bg-success]="o.status==='Delivered'"
                        [class.bg-danger]="o.status==='Cancelled'">
                        {{ o.status }}
                  </span>
                </td>
                <td class="text-end d-none d-sm-table-cell">{{ o.date }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="col-12 col-xl-5">
      <div class="card border-0 h-100">
        <div class="card-header bg-white border-0 fw-semibold">↩️ Recent Returns</div>
        <div class="table-responsive">
          <table class="table table-hover align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th>Return #</th>
                <th class="d-none d-md-table-cell">Customer</th>
                <th class="text-end">Amount</th>
                <th class="d-none d-lg-table-cell">Reason</th>
                <th class="text-end d-none d-xl-table-cell">Date</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let r of recentReturns">
                <td>{{ r.id }}</td>
                <td class="d-none d-md-table-cell">{{ r.customer }}</td>
                <td class="text-end">{{ r.amount | number }}</td>
                <td class="d-none d-lg-table-cell">{{ r.reason }}</td>
                <td class="text-end d-none d-xl-table-cell">{{ r.date }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div> -->
</div>